import { apiRequest } from '../lib/api-client';

/** Customer success: the portfolio, one account's health and its onboarding plan. */

export const HEALTH_BANDS = ['HEALTHY', 'WATCH', 'AT_RISK', 'CRITICAL'] as const;

export type HealthBand = (typeof HEALTH_BANDS)[number];

export const RENEWAL_STATUSES = ['PENDING', 'AT_RISK', 'RENEWED', 'CHURNED'] as const;

export type RenewalStatus = (typeof RENEWAL_STATUSES)[number];

export interface SignalScore {
  signal: 'ENGAGEMENT' | 'SUPPORT' | 'SATISFACTION' | 'COMMERCIAL' | 'ONBOARDING';
  score: number | null;
  detail: Record<string, number | null>;
}

export interface Health {
  score: number;
  band: HealthBand;
  signals: SignalScore[];
  coverage: number;
}

export interface CustomerAccount {
  id: string;
  companyId: string;
  companyName: string;
  csmId: string | null;
  startedAt: string;
  mrrCents: number | null;
  renewalDate: string | null;
  renewalStatus: RenewalStatus;
  daysToRenewal: number | null;
  healthScore: number | null;
  healthBand: HealthBand | null;
  healthComputedAt: string | null;
  churnReason: string | null;
  notes: string | null;
  ownerId: string | null;
  createdAt: string;
}

export interface AccountDetail extends CustomerAccount {
  health: Health | null;
  trend: { on: string; score: number; band: HealthBand }[];
}

export interface PortfolioSummary {
  managed: number;
  byBand: Record<HealthBand, number>;
  mrrCents: number;
  atRiskMrrCents: number;
  renewingIn30Days: number;
  overdueRenewals: number;
  churnedThisQuarter: number;
  unscored: number;
}

export interface AccountQuery {
  band?: HealthBand[];
  renewalStatus?: RenewalStatus[];
  renewalWithinDays?: number;
  mine?: boolean;
  search?: string;
  sort?: 'health' | 'renewal' | 'value' | 'name';
  limit?: number;
  offset?: number;
}

function queryString(query: AccountQuery): string {
  const params = new URLSearchParams();

  for (const band of query.band ?? []) params.append('band', band);
  for (const status of query.renewalStatus ?? []) params.append('renewalStatus', status);

  if (query.renewalWithinDays !== undefined) {
    params.set('renewalWithinDays', String(query.renewalWithinDays));
  }
  if (query.mine) params.set('mine', 'true');
  if (query.search) params.set('search', query.search);
  if (query.sort) params.set('sort', query.sort);
  if (query.limit !== undefined) params.set('limit', String(query.limit));
  if (query.offset !== undefined) params.set('offset', String(query.offset));

  return params.toString();
}

export function listAccounts(
  query: AccountQuery,
): Promise<{ accounts: CustomerAccount[]; total: number }> {
  return apiRequest(`/v1/cs/accounts?${queryString(query)}`);
}

export function portfolioSummary(): Promise<PortfolioSummary> {
  return apiRequest('/v1/cs/summary');
}

export function getAccount(id: string): Promise<AccountDetail> {
  return apiRequest(`/v1/cs/accounts/${id}`);
}

export function startManaging(payload: {
  companyId: string;
  csmId?: string | null;
  mrrCents?: number | null;
  renewalDate?: string | null;
}): Promise<CustomerAccount> {
  return apiRequest('/v1/cs/accounts', { method: 'POST', body: payload });
}

export function patchAccount(
  id: string,
  payload: {
    csmId?: string | null;
    mrrCents?: number | null;
    renewalDate?: string | null;
    renewalStatus?: RenewalStatus;
    churnReason?: string | null;
    notes?: string | null;
  },
): Promise<CustomerAccount> {
  return apiRequest(`/v1/cs/accounts/${id}`, { method: 'PATCH', body: payload });
}

export function rescoreAccount(id: string): Promise<AccountDetail> {
  return apiRequest(`/v1/cs/accounts/${id}/rescore`, { method: 'POST' });
}

export interface OnboardingTemplate {
  id: string;
  name: string;
  description: string | null;
  active: boolean;
  stepCount: number;
  steps?: {
    id: string;
    title: string;
    description: string | null;
    offsetDays: number;
    assigneeId: string | null;
  }[];
}

export function listTemplates(): Promise<{ templates: OnboardingTemplate[] }> {
  return apiRequest('/v1/cs/onboarding/templates');
}

export function getTemplate(id: string): Promise<OnboardingTemplate> {
  return apiRequest(`/v1/cs/onboarding/templates/${id}`);
}

export function createTemplate(payload: {
  name: string;
  description?: string | null;
  steps: { title: string; offsetDays?: number }[];
}): Promise<OnboardingTemplate> {
  return apiRequest('/v1/cs/onboarding/templates', { method: 'POST', body: payload });
}

export interface OnboardingPlan {
  id: string;
  accountId: string;
  name: string;
  status: 'RUNNING' | 'COMPLETED' | 'ABANDONED';
  startedOn: string;
  steps: {
    id: string;
    taskId: string | null;
    title: string;
    dueDate: string | null;
    doneAt: string | null;
    assigneeId: string | null;
  }[];
  done: number;
  total: number;
  overdue: number;
}

export function getPlan(accountId: string): Promise<{ plan: OnboardingPlan | null }> {
  return apiRequest(`/v1/cs/accounts/${accountId}/onboarding`);
}

export function startOnboarding(accountId: string, templateId: string): Promise<OnboardingPlan> {
  return apiRequest(`/v1/cs/accounts/${accountId}/onboarding`, {
    method: 'POST',
    body: { templateId },
  });
}

export function finishOnboarding(
  accountId: string,
  status: 'COMPLETED' | 'ABANDONED',
): Promise<void> {
  return apiRequest(`/v1/cs/accounts/${accountId}/onboarding/finish`, {
    method: 'POST',
    body: { status },
  });
}

export function bandTone(band: HealthBand | null): 'neutral' | 'positive' | 'warning' | 'danger' {
  if (band === 'HEALTHY') return 'positive';
  if (band === 'WATCH') return 'neutral';
  if (band === 'AT_RISK') return 'warning';
  if (band === 'CRITICAL') return 'danger';

  return 'neutral';
}

/**
 * How a renewal reads on a list.
 *
 * A date that has passed with the renewal still pending is the one worth shouting about: a renewal
 * nobody actioned is a customer who is, as far as any system knows, no longer paying.
 */
export function renewalTone(
  account: Pick<CustomerAccount, 'renewalStatus' | 'daysToRenewal'>,
): 'neutral' | 'positive' | 'warning' | 'danger' {
  if (account.renewalStatus === 'CHURNED') return 'danger';
  if (account.renewalStatus === 'RENEWED') return 'positive';
  if (account.daysToRenewal === null) return 'neutral';
  if (account.daysToRenewal < 0) return 'danger';
  if (account.daysToRenewal <= 30) return 'warning';

  return 'neutral';
}

// ---------------------------------------------------------------------------
// Playbooks (Phase 50)
// ---------------------------------------------------------------------------

export interface Playbook {
  key: string;
  trigger: string;
  actions: string[];
  conditions: number;
}

export function listPlaybooks(): Promise<{ playbooks: Playbook[] }> {
  return apiRequest('/v1/cs/playbooks');
}

/** Creates an ordinary automation from a starter. It arrives inactive, like any other. */
export function applyPlaybook(key: string, name: string): Promise<{ id: string }> {
  return apiRequest(`/v1/cs/playbooks/${key}/apply`, { method: 'POST', body: { name } });
}
